import React, { useEffect } from 'react';
import { useOpenFgaPermission } from '../../../hooks/useOpenFgaPermission';

/**
 * Component that checks permissions and hides tabs accordingly
 * 
 * For components:
 * - Without can_manage_config: only shows Overview tab
 * - Hides: CI/CD, Config Editor, Docs tabs
 * 
 * For projects:
 * - Without can_manage_config or projectTabAllowed: only shows Project tab
 * - Hides: Applications, Team, Jira tabs
 * 
 * For applications:
 * - Without can_view_details: only shows Repositories tab
 * - Hides: Projects, Code Quality tabs
 * 
 * Should be included in the overview or a common area that always renders
 */
export const TabPermissionGuard: React.FC = () => {
  // Permiso general para tabs de configuración, CI/CD, Docs (solo para componentes)
  const { allowed: allowedConfig, loading: loadingConfig } = useOpenFgaPermission('can_manage_config', 'component');
  // Permiso para tabs de proyecto (Applications, Team, Jira)
  const { allowed: allowedProject, loading: loadingProject } = useOpenFgaPermission('can_view_details', 'project');
  // Permiso para tabs de aplicación (Projects, Code Quality)
  const { allowed: allowedApplication, loading: loadingApplication } = useOpenFgaPermission('can_view_details', 'application');

  useEffect(() => {
    // Track if we've done the initial setup for project tabs

    const shouldTabBeVisible = (tab: Element, hasPermissions: boolean, isInitialSetup: boolean): boolean | null => {
      const text = tab.textContent?.toLowerCase() || '';
      const href = tab.getAttribute('href') || '';

      // Manage entity-level tabs (CI/CD, Config, Docs), project page tabs (Applications, Team, Jira), 
      // and application page tabs (Projects, Code Quality)
      const isConfigTab = href.endsWith('/config');
      const isCICDTab = href.endsWith('/ci-cd');
      const isDocsTab = text.includes('docs') &&
        (href.endsWith('/docs') || text === 'docs');
      const isApplicationsTab = text.includes('applications') || href.endsWith('/applications');
      const isTeamTab = text.includes('team') || href.endsWith('/team');
      const isJiraTab = (text.includes('jira') || href.endsWith('/jira')) && !text.includes('jira 2');
      const isProjectsTab = text.includes('projects') && href.endsWith('/projects');
      const isCodeQualityTab = (text.includes('code quality') || text.includes('sonarqube')) || href.endsWith('/sonarqube');

      // Return true if should be visible, false if should be hidden, null if not affected
      if (isConfigTab || isCICDTab || isDocsTab) {
        return hasPermissions;
      }
      // Tabs de proyecto
      if (isApplicationsTab || isTeamTab || isJiraTab) {
        if (isInitialSetup) {
          return allowedProject;
        }
        if (!allowedProject) {
          return false;
        }
        return null;
      }
      // Tabs de aplicación
      if (isProjectsTab || isCodeQualityTab) {
        if (isInitialSetup) {
          return allowedApplication;
        }
        if (!allowedApplication) {
          return false;
        }
        return null;
      }
      return null;
    };

    const processTab = (tab: Element, hasPermissions: boolean, isInitialSetup: boolean = false) => {
      const shouldBeVisible = shouldTabBeVisible(tab, hasPermissions, isInitialSetup);

      if (shouldBeVisible === null) {
        // This tab is not managed by this guard
        return;
      }

      const currentDisplay = (tab as HTMLElement).style.display;
      const targetDisplay = shouldBeVisible ? '' : 'none';

      // Only update if needed
      if (currentDisplay !== targetDisplay) {
        (tab as HTMLElement).style.display = targetDisplay;
      }
    };

    const processTabs = (hasPermissions: boolean, isInitialSetup: boolean = false) => {
      const allTabs = document.querySelectorAll('button[role="tab"], a[role="tab"]');
      allTabs.forEach(tab => processTab(tab, hasPermissions, isInitialSetup));
    };

    if (!loadingConfig && !loadingProject && !loadingApplication) {
      // Initial processing with a small delay to ensure tabs are rendered
      const initialTimeout = setTimeout(() => {
        processTabs(allowedConfig, true); // Mark as initial setup
      }, 100);

      // Use MutationObserver to detect new tabs being added to the DOM
      const observer = new MutationObserver((mutations) => {
        let shouldProcess = false;

        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // Check if the added node is a tab or contains tabs
              if (element.matches('button[role="tab"], a[role="tab"]') ||
                element.querySelectorAll('button[role="tab"], a[role="tab"]').length > 0) {
                shouldProcess = true;
              }
            }
          });
        });

        // Only process if we detected relevant changes
        if (shouldProcess) {
          // After initial setup, don't mark as initial anymore
          processTabs(allowedConfig, false);
        }
      });

      // Observe only the main content area to reduce noise
      const mainContent = document.querySelector('main') || document.body;
      observer.observe(mainContent, {
        childList: true,
        subtree: true,
      });

      return () => {
        clearTimeout(initialTimeout);
        observer.disconnect();
      };
    }
    return () => { };
  }, [loadingConfig, allowedConfig, loadingProject, allowedProject, loadingApplication, allowedApplication]);

  // This component doesn't render anything
  return null;
};
